import { CALENDAR_TYPES } from '@/views/constants'
import Calendar from '@/entities/Calendar'
import CalendarBorrowedProviders from '@/entities/CalendarBorrowedProviders'
import CalendarBorrowedLocations from '@/entities/CalendarBorrowedLocations'
import CalendarDisplayInfo from '@/entities/CalendarDisplayInfo'
import CalendarSortingInfo from '@/entities/CalendarSortingInfo'

/*
 always returns object of doctorLocations in next format
 {
   [doctor.id]: {
     [location.id]: 1
   }
 }
 */
function standardizeDocLocs (allDocLocs) {
  if (!Array.isArray(allDocLocs)) {
    return allDocLocs
  }
  return allDocLocs.reduce((acc, locDocItem) => {
    acc[locDocItem.doctor_id] = {
      [locDocItem.location_id]: 1
    }
    return acc
  }, {})
}

/*
 always returns object of doctorLocations in next format
 {
   [location.id]: 'location name  - street, city, state'
 }
 note: location name from this object not used to diaply enywhere in the widget
 */
function standardizeLocs (allLocs) {
  // if practice have only 1 location standardize data to object
  if (!Array.isArray(allLocs)) {
    return allLocs
  }
  return allLocs.reduce((acc, loc) => {
    acc[loc.id] = `${loc.address_street}, ${loc.city}, ${loc.state}`.trim()
    return acc
  }, {})
}

// get first doctorId for locationId or return 0
function getFirstDoctorId (locationId, standardizedDocLocs) {
  let res = 0
  const docIdsList = Object.keys(standardizedDocLocs || {})
  const docIdsForLocation = docIdsList.reduce((acc, docId) => {
    // location valid if locationId not set or this provider mapped to selected location
    if (standardizedDocLocs[docId][locationId] || !locationId) {
      acc.push(docId)
    }
    return acc
  }, [])
  if (docIdsForLocation.length > 0) {
    res = docIdsForLocation[0]
  }
  return +res
}
// get first locationId for providerId or return 0 if such provider has no locations, or there was no provider
function getFirstLocationId (providerId = 0, standardizedDocLocs = {}, standardizedLocs = {}) {
  let res = 0
  const locIdsList = Object.keys(standardizedLocs)
  const locIdsForProvider = locIdsList.reduce((acc, locId) => {
    // location valid if providerId not set or this locationId mapped to selected provider
    if (!providerId || providerId === 0 || Boolean(standardizedDocLocs[providerId][locId])) {
      acc.push(locId)
    }
    return acc
  }, [])
  if (locIdsForProvider.length > 0) {
    res = locIdsForProvider[0]
  }
  return +res
}

/*
 creates provider_location pairs for each calendar
 and base on this pairs(actually it's calendarIndex) creates calendarInfo object
 that will be used as option for Calendar entity constructor
 */
function prepareCalendarsDataForProcessing (initCalendarsData, standardizedDocLocs, standardizedLocs, formattedPhone, phone) {
  const providerLocationCalendarsPairs = []
  const calendarsInfoForProcessing = {}
  const providerIndexes = []
  const locationIndexes = []

  initCalendarsData.forEach(calendar => {
    if (calendar.type === CALENDAR_TYPES.PROVIDER_LOCATION_TYPE) {
      addProviderLocationCalendarPair(calendar.location_id, calendar.doctor_id, calendar.id)
    } else if (calendar.type === CALENDAR_TYPES.PROVIDER_TYPE) {
      addProviderLocationCalendarPair(getFirstLocationId(calendar.doctor_id, standardizedDocLocs, standardizedLocs), calendar.doctor_id, calendar.id)
    } else if (calendar.type === CALENDAR_TYPES.LOCATION_TYPE) {
      addProviderLocationCalendarPair(calendar.location_id, getFirstDoctorId(calendar.location_id, standardizedDocLocs, calendar.id), calendar.id)
    } else if (calendar.type === CALENDAR_TYPES.PRACTICE_TYPE) {
      const firstProviderId = getFirstDoctorId()
      const locationId = getFirstLocationId(firstProviderId, standardizedDocLocs, standardizedLocs)
      addProviderLocationCalendarPair(locationId, firstProviderId || getFirstDoctorId(locationId, standardizedDocLocs), calendar.id)
    }
  })
  return {
    providerLocationCalendarsPairs,
    calendarsInfoForProcessing,
    providerIndexes,
    locationIndexes
  }

  function addProviderLocationCalendarPair (locationId, providerId = 0, calendarId) {
    if (!locationId) {
      return
    }
    providerIndexes.indexOf(providerId) < 0 && providerIndexes.push(providerId)
    locationIndexes.indexOf(locationId) < 0 && locationIndexes.push(locationId)
    providerLocationCalendarsPairs.push({
      locationId,
      providerId,
      calendarId
    })
    calendarsInfoForProcessing[`${providerId}_${locationId}`] = collectCalendarInfoForProcessing(calendarId, providerId, locationId, initCalendarsData, formattedPhone, phone)
  }
}

// creates calendar options object, that will be used fot Calendar entity creating
function collectCalendarInfoForProcessing (calendarId, providerId, locationId, rawCalendarsData, formattedPhone, phone) {
  const calendarMap = rawCalendarsData.find(calendar => {
    return calendarId === calendar.id || ((!calendar.doctor_id || +calendar.doctor_id === +providerId) && (!calendar.location_id || +calendar.location_id === +locationId))
  })
  let detailsOptions = {
    type: null,
    skip_cal_provider_sel: 0,
    external_calendar_source: null,
    appt_phone_formatted: formattedPhone,
    appt_phone: phone,
    allow_no_ins_answer: true
  }
  if (calendarMap) {
    detailsOptions = {
      ...detailsOptions,
      ...calendarMap.calendar_owner,
      practice: {
        ...calendarMap?.calendar_owner?.practice,
        ...calendarMap?.practice
      },
      type: calendarMap.type,
      skip_cal_provider_sel: calendarMap.practice?.skip_cal_provider_sel ?? 0,
      external_calendar_source: calendarMap.external_calendar_source ?? null,
      allow_no_ins_answer: Boolean(calendarMap.allow_no_ins_answer),
      communication_settings: calendarMap.communication_settings ?? null,
      field_settings: calendarMap.field_settings,
      telehealth: calendarMap?.show_telehealth || false,
      screening_settings: calendarMap?.screening_settings || '',
      id: calendarMap?.id ?? null,
      schedule_id: calendarMap?.schedule_id ?? null,
      provider_id: providerId,
      location_id: locationId,
      calendar_label: calendarMap.calendar_label,
      calendar_text: calendarMap.calendar_copy
    }
  }
  return detailsOptions
}

// using collected above info(calendar options object) create Calendars(now, just with a base or "simple" info)
function generateCalendarsWithBaseInfo (provLocPairs, calendarsInfoForProcessing) {
  const res = {}
  provLocPairs.forEach((calendarKeyInfo) => {
    const calIndex = `${calendarKeyInfo.providerId}_${calendarKeyInfo.locationId}`
    const options = calendarsInfoForProcessing[calIndex]
    res[calIndex] = new Calendar(options)
  })
  return res
}

// collect allLocations, allProviders and other related to it info across all Calendars
function getPracticeProvidersLocationsInfo (calendars) {
  const allPracticeLocations = []
  const allPracticeProviders = []
  const calendarIndexToLocationAndProviderMap = {}
  const calendarIdToIndexMap = {}
  const savedLocations = new Set()
  const savedProviders = new Set()

  for (const calendarIndex in calendars) {
    const calendar = calendars[calendarIndex]
    calendarIdToIndexMap[calendar.id] = calendarIndex
    const info = collectLocationsProvidersInfoPerCalendar(calendar.originalLocations, calendar.originalProviders, calendar.type)
    calendarIndexToLocationAndProviderMap[calendarIndex] = {
      original: info,
      displayed: info // will be updated in the next step(after borrowed data will be collected)
    }
    calendarIndexToLocationAndProviderMap[calendarIndex].original.allLocations.list.forEach(location => {
      if (!savedLocations.has(location.id)) {
        allPracticeLocations.push(location)
        savedLocations.add(location.id)
      }
    })
    calendarIndexToLocationAndProviderMap[calendarIndex].original.allProviders.list.forEach(provider => {
      if (!savedProviders.has(provider.id)) {
        allPracticeProviders.push(provider)
        savedProviders.add(provider.id)
      }
    })
  }

  return {
    allPracticeLocations,
    allPracticeProviders,
    calendarIndexToLocationAndProviderMap,
    calendarIdToIndexMap
  }
}

// based on calendar type, locations and providers info, create lists of all and filterable calendar locations/provider-ids
function collectLocationsProvidersInfoPerCalendar (locations, providers, calendarType, showAsPractice = false) {
  const allLocationIds = []
  const filterableLocationIds = []
  const allProviderIds = []
  const filterableProviderIds = []
  const filterableLocationsList = []
  const filterableProvidersList = []

  locations.list.forEach(location => {
    allLocationIds.push(location.id)
    if ([CALENDAR_TYPES.PROVIDER_LOCATION_TYPE, CALENDAR_TYPES.LOCATION_TYPE].includes(calendarType)) {
      filterableLocationIds.push(location.id)
      filterableLocationsList.push({
        ...location
      })
    }
  })

  providers.list.forEach(provider => {
    allProviderIds.push(provider.id)
    if ([CALENDAR_TYPES.PROVIDER_LOCATION_TYPE, CALENDAR_TYPES.PROVIDER_TYPE].includes(calendarType) && !showAsPractice) {
      filterableProviderIds.push(provider.id)
      filterableProvidersList.push({
        ...provider
      })
    }
  })
  return {
    allLocationIds: allLocationIds,
    allProviderIds: allProviderIds,
    filterableLocationIds: filterableLocationIds,
    filterableProviderIds: filterableProviderIds,
    allLocations: locations,
    allProviders: providers,
    filterableLocationsList: filterableLocationsList,
    filterableProvidersList: filterableProvidersList
  }
}

/*
 update calendarIndex to location and provider map with display information
   cause during the 1 run only original locations and providers info was used,
   then, based on this map calendars were filled with borrowed info and displayInfo was recalculated
 display info need for filtering calculation on stepA
 */
function updatePracticeToProvidersLocationsMapWithDisplayInfo (calendars, calendarIndexToLocationAndProviderMap) {
  const calendarIndexToLocationAndProviderMapNew = calendarIndexToLocationAndProviderMap

  for (const calendarIndex in calendars) {
    const calendar = calendars[calendarIndex]
    calendarIndexToLocationAndProviderMapNew[calendarIndex].displayed = collectLocationsProvidersInfoPerCalendar(calendar.displayInfo.displayLocations, calendar.displayInfo.displayProviders, calendar.type, calendar.settings.communication.showAsPractice)
  }
  return calendarIndexToLocationAndProviderMapNew
}

/*
 here each calendar will collect borrowed info (if availability_as settings set to "other calendar")
  display info recalculates based on borrowed data(if it exist)
  sorting indexes also need to be rebuild after borrowed data collecting
 */
function updateCalendarsWithRemainInfo (calendars, calendarIndexToLocationAndProviderMap, calendarIdToIndexMap, calendarsInfoForProcessing, providerIndexes, locationIndexes) {
  for (const calIndex in calendars) {
    const calendar = calendars[calIndex]
    calendar.borrowedProviders = new CalendarBorrowedProviders(calendar.type, calendar.settings.communication, calendarIndexToLocationAndProviderMap, calendarIdToIndexMap)
    calendar.borrowedLocations = new CalendarBorrowedLocations(calendar.type, calendar.settings.communication, calendarIndexToLocationAndProviderMap, calendarIdToIndexMap)
    calendar.displayInfo = new CalendarDisplayInfo(calendar, calendarsInfoForProcessing[calIndex])
    calendar.sortingInfo = new CalendarSortingInfo(calendar, providerIndexes, locationIndexes)
  }

  return calendars
}

// main processor function which unites all separated logic above in a one mechanism
export default (initCalendarsData, allDocLocs, allLocs, formattedPhone = '', phone = '') => {
  let calendars = []
  const standardizedDocLocs = standardizeDocLocs(allDocLocs)
  const standardizedLocs = standardizeLocs(allLocs)

  // create main info for Calendars creating
  const {
    providerLocationCalendarsPairs = [],
    calendarsInfoForProcessing = {},
    providerIndexes = [],
    locationIndexes = []
  } = prepareCalendarsDataForProcessing(initCalendarsData, standardizedDocLocs, standardizedLocs, formattedPhone, phone)

  // create Calendars using info collected above
  calendars = generateCalendarsWithBaseInfo(providerLocationCalendarsPairs, calendarsInfoForProcessing)

  // collect calendarIndex to locations and providers map based on Calendars
  let {
    allPracticeLocations = [],
    allPracticeProviders = [],
    calendarIndexToLocationAndProviderMap = {},
    calendarIdToIndexMap = {}
  } = getPracticeProvidersLocationsInfo(calendars)

  /*
   update Calendars with borrowed providers|locations,
   recalculate display info based on borrowed data(if exist)
   recalculate sorting indexes based on borrowed data(if exist)
   */
  calendars = updateCalendarsWithRemainInfo(calendars, calendarIndexToLocationAndProviderMap, calendarIdToIndexMap, calendarsInfoForProcessing, providerIndexes, locationIndexes)

  // update  calendarIndex to locations and providers map with displayed info(cause it can display borrowed info) for each calendar
  calendarIndexToLocationAndProviderMap = updatePracticeToProvidersLocationsMapWithDisplayInfo(calendars, calendarIndexToLocationAndProviderMap)
  return {
    standardizedDocLocs,
    initCalendarsData,
    calendars,
    calendarIndexToLocationAndProviderMap,
    calendarIdToIndexMap,
    allPracticeLocations,
    allPracticeProviders
  }
}
